import React, { useState, useEffect, useCallback } from 'react';
import { TemplateUploadDialog } from './TemplateUploadDialog';
import { REPORT_TYPE_LABELS } from '../constants/reportTypes';

export interface ProposalTemplateSummary {
  id: string;
  title: string;
  description: string | null;
  originalFileName: string;
  originalFileType: 'pdf' | 'docx' | 'xlsx';
  usageCount: number;
  createdAt: string;
  reportTypes: string[];
  isSystemDefault: boolean;
  defaultForReportType: string | null;
  previewS3Key: string | null;
}

interface TemplateLibraryViewProps {
  isAdmin: boolean;
  token: string | null;
}

const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

function getReportTypeLabel(type: string): string {
  return REPORT_TYPE_LABELS[type] || type;
}

export const TemplateLibraryView: React.FC<TemplateLibraryViewProps> = ({ isAdmin, token }) => {
  const [templates, setTemplates] = useState<ProposalTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewType, setPreviewType] = useState<'pdf' | 'docx'>('pdf');
  const [showUpload, setShowUpload] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ProposalTemplateSummary | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/proposals/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        if (!selectedId && data.templates?.length > 0) {
          setSelectedId(data.templates[0].id);
        }
      } else {
        setError('Failed to load templates');
      }
    } catch {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchPreview = useCallback(async (templateId: string) => {
    if (!token) return;
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await fetch(`${apiUrl}/api/proposals/templates/${templateId}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError('Failed to load preview');
        return;
      }
      const contentType = res.headers.get('content-type') || '';
      setPreviewType(contentType.includes('pdf') ? 'pdf' : 'docx');
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      setError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedId) {
      fetchPreview(selectedId);
    }
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, fetchPreview]);

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!window.confirm('Delete this template? (Deleted default templates will be recreated automatically.)')) return;
    try {
      const res = await fetch(`${apiUrl}/api/proposals/templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        if (selectedId === id) setSelectedId(null);
        fetchTemplates();
      } else {
        setError('Failed to delete template');
      }
    } catch {
      setError('Failed to delete template');
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedId) || null;

  return (
    <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-md border border-gray-200 dark:border-dark-border">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Proposal Templates</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isAdmin
              ? 'Upload and manage proposal templates for your team. Default templates can be edited; once edited they become custom.'
              : 'Preview the proposal templates available to your team. Templates are managed by your company admin.'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Upload Template
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-bold">×</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No proposal templates available yet.</p>
          {isAdmin && <p className="text-sm mt-1">Upload a template to get started.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Template list */}
          <div className="lg:col-span-2 space-y-2 max-h-[70vh] overflow-y-auto">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedId(template.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedId === template.id
                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{template.title}</h3>
                      {template.isSystemDefault && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full whitespace-nowrap">
                          Default
                        </span>
                      )}
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded uppercase">
                        {template.originalFileType}
                      </span>
                    </div>
                    {template.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{template.description}</p>
                    )}
                    {template.reportTypes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.reportTypes.map((rt) => (
                          <span
                            key={rt}
                            className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded"
                          >
                            {getReportTypeLabel(rt)}
                          </span>
                        ))}
                      </div>
                    )}
                    {template.reportTypes.length === 0 && !template.isSystemDefault && (
                      <span className="text-xs text-gray-400 mt-1 inline-block">All report types</span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditTemplate(template);
                        }}
                        className="p-1 text-gray-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(template.id);
                        }}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Preview pane */}
          <div className="lg:col-span-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 min-h-[500px]">
            {!selectedTemplate ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400 p-8">
                Select a template to preview.
              </div>
            ) : previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : !previewUrl ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400 p-8">
                Preview not available yet.
                {(selectedTemplate.originalFileType === 'docx' || selectedTemplate.originalFileType === 'xlsx') && (
                  <span className="ml-1">{selectedTemplate.originalFileType.toUpperCase()} preview is still being generated — check back in a moment.</span>
                )}
              </div>
            ) : previewType === 'pdf' ? (
              <iframe
                src={previewUrl}
                title={selectedTemplate.title}
                className="w-full h-[70vh] rounded-lg"
              />
            ) : (
              <div className="p-8 text-sm text-gray-500 dark:text-gray-400">
                Preview unavailable for this format.
                <a
                  href={previewUrl}
                  download={selectedTemplate.originalFileName}
                  className="ml-2 text-primary hover:underline"
                >
                  Download the template
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {showUpload && (
        <TemplateUploadDialog
          token={token}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            fetchTemplates();
          }}
        />
      )}

      {editTemplate && (
        <TemplateUploadDialog
          token={token}
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onUploaded={() => {
            setEditTemplate(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default TemplateLibraryView;
